import React from 'react';
import { Button } from './ui/button';
import { ChevronDown, MessageCircle, UtensilsCrossed, FileText } from 'lucide-react';
import { restaurantInfo, videoUrl } from '../data/mock';

const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-label="Hero Section">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          poster="https://customer-assets.emergentagent.com/job_japamix-anapolys/artifacts/yxysotlz_PHOTO-2026-01-20-14-00-27.jpg">

          <source src={videoUrl} type="video/mp4" />
        </video>
        {/* Reduced overlay opacity for better video visibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-black/40 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-t from-neutral-950 to-transparent" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center border border-amber-600/40 animate-fade-in gap-2 px-4 py-2 mb-8 rounded-full !bg-[rgba(239,239,239,0.01)]">
            <span className="w-2 h-2 animate-pulse rounded-full !bg-[#F9F9F9]" />
            <span className="uppercase !font-medium !text-sm !text-[#F4F4F4]">
              Anápolis - GO
            </span>
          </div>

          {/* Main Title - H1 SEO Optimized */}
          <h1 className="md:text-7xl lg:text-8xl animate-fade-in-up !font-serif !font-bold !text-xl !mb-[25px] text-white">
            {restaurantInfo.tagline}
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-2xl mx-auto font-light leading-relaxed animate-fade-in-up stagger-1">
            {restaurantInfo.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up stagger-2">
            <Button
              size="lg"
              className="bg-amber-700 hover:bg-amber-800 text-white font-semibold px-8 py-6 text-lg btn-hover-lift flex items-center gap-2 w-full sm:w-auto"
              onClick={() => window.open(restaurantInfo.deliveryUrl, '_blank')}
              aria-label="Pedir delivery online">

              <UtensilsCrossed size={20} />
              Pedir Delivery
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-neutral-900 font-semibold px-8 py-6 text-lg btn-hover-lift flex items-center gap-2 w-full sm:w-auto bg-transparent"
              onClick={() => window.open(restaurantInfo.menuUrl, '_blank')}
              aria-label="Ver cardápio do Japamix">

              <FileText size={20} />
              Ver Cardápio
            </Button>

            <Button
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-6 text-lg btn-hover-lift flex items-center gap-2 w-full sm:w-auto"
              onClick={() => window.open(`https://wa.me/${restaurantInfo.whatsapp}`, '_blank')}
              aria-label="Contato via WhatsApp">

              <MessageCircle size={20} />
              WhatsApp
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={() => scrollToSection('#sobre')}
          className="text-white/60 hover:text-white transition-colors"
          aria-label="Rolar para seção sobre">

          <ChevronDown size={40} />
        </button>
      </div>

      {/* Side decorative elements */}
      <div className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2">
        <div className="flex flex-col gap-4 items-center">
          <div className="w-px h-24 bg-gradient-to-b from-transparent via-amber-500 to-transparent" />
          <span className="text-amber-400 text-xs uppercase tracking-widest font-medium" style={{ writingMode: 'vertical-rl' }}>
            Culinária Japonesa
          </span>
          <div className="w-px h-24 bg-gradient-to-b from-transparent via-amber-500 to-transparent" />
        </div>
      </div>

      <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2">
        <div className="flex flex-col gap-4 items-center">
          <div className="w-px h-24 bg-gradient-to-b from-transparent via-amber-600 to-transparent" />
          <span className="text-amber-500 text-xs uppercase tracking-widest font-medium" style={{ writingMode: 'vertical-rl' }}>
            Desde 2020
          </span>
          <div className="w-px h-24 bg-gradient-to-b from-transparent via-amber-600 to-transparent" />
        </div>
      </div>
    </section>);

};

export default HeroSection;